import { petcoPewterBlue } from '@common/_assets/color-constants.js';
import { FormInput, spreadAndtransformDefaultValues } from '@common/components/forms';
import { YesNoConfirmModal } from '@common/components/modals';
import { useUnsavedChangesWarning } from '@common/hooks';
import { useUniqueKey } from '@common/hooks/useUniqueKey';
import { HospitalModel, HospitalScheduleTemplateModel, schedule } from '@common/models';
import { EntityScheduleType } from '@common/models/Enums';
import { EntitySchedulePreference } from '@common/models/Schedule';
import { HospitalScheduleTemplateRequest, IHospitalScheduleTemplateRequest } from '@common/models/requests/HospitalScheduleTemplateRequest';
import { some } from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { SchedulePreferencesUIStore, SchedulingPreferences } from './SchedulePreferences';

export type LocationScheduleTemplateModalProps = {
    show: boolean,
    hospital: HospitalModel,
    existingTemplateNames: (string | undefined)[],
    hospitalScheduleTemplate: HospitalScheduleTemplateModel | undefined,
    onSaveTemplate: (request: IHospitalScheduleTemplateRequest) => void;
    onClose: () => void,
}

export const LocationScheduleTemplateModal = observer(({ show, existingTemplateNames, hospitalScheduleTemplate, onSaveTemplate, onClose, hospital }: LocationScheduleTemplateModalProps) => {
    const calendarKey = useUniqueKey([hospitalScheduleTemplate]);
    const [isNewTemplate] = React.useState<boolean>(hospitalScheduleTemplate === undefined || hospitalScheduleTemplate.hospitalScheduleTemplateId === 0 ? true : false);
    const [schedulePreferenceUI, setSchedulePreferenceUI] = React.useState<SchedulePreferencesUIStore | undefined>(undefined);
    const [schedulePreferences] = React.useState<EntitySchedulePreference[]>(hospitalScheduleTemplate?.hospitalSchedulePreferences ?? []);
    const [scheduleTemplateId] = React.useState<number>(hospitalScheduleTemplate?.hospitalScheduleTemplateId ?? 0);
    const [showCancelConfirm, setShowCancelConfirm] = React.useState<boolean>(false);

    const [formData] = React.useMemo(() => {
        return [isNewTemplate ? new HospitalScheduleTemplateRequest(
            { hospitalScheduleTemplateId: 0, isActive: true, isDefault: false, color: petcoPewterBlue, name: '', hospitalId: hospital.hospitalId, rowVersion: '' })
            : new HospitalScheduleTemplateRequest(hospitalScheduleTemplate)];
    }, [hospitalScheduleTemplate, isNewTemplate, hospital]);

    const { control, handleSubmit, watch, formState: { isDirty } } = useForm<HospitalScheduleTemplateRequest>({ defaultValues: spreadAndtransformDefaultValues(formData) });
    const unsavedChangesPrompt = useUnsavedChangesWarning(isDirty);

    const raiseOnSaveTemplate = React.useCallback((template: HospitalScheduleTemplateRequest) => {
        if (!schedulePreferenceUI || !onSaveTemplate)
            return;
        const preferences = schedulePreferenceUI.eventSchedulePreferences;
        template.color = schedulePreferenceUI.schedulingColor;
        template.hospitalSchedulePreferences = preferences ?? [] as schedule.EntitySchedulePreference[];
        onSaveTemplate(template);
    }, [schedulePreferenceUI, onSaveTemplate]);

    const checkConfirmDirty = () => {
        if (isDirty || schedulePreferenceUI?.hasChanges)
            setShowCancelConfirm(true);
        else
            onClose();
    };

    const validateName = (value: any) => {
        //TODO Add new Search End criteria to search service to allow searching by template name (exactly).  Do a different search in validateName
        if (value && typeof (value) === 'string') {
            const ret = ((!isNewTemplate && (value as string) === hospitalScheduleTemplate!.name) || !some(existingTemplateNames ?? [], p => p === value as string));
            if (ret)
                return true;
        }
        return 'That name is already in use, please choose another or edit the existing template.';
    };

    const watchColor = watch("color", undefined);
    const watchName = watch("name", "");

    return (
        <>
            {unsavedChangesPrompt}
            <YesNoConfirmModal
                visible={showCancelConfirm}
                title="Cancel Changes"
                description="You have unsaved changes, you are sure you want to cancel?"
                busy={false}
                cancelButtonText='No'
                onConfirm={() => onClose()}
                onCancel={() => { setShowCancelConfirm(false) }}
            />
            <Modal size="xl" centered show={show} onHide={checkConfirmDirty} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{!isNewTemplate ? 'Edit template schedule preferences' : 'Create new template schedule preferences'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form id="locationPreferenceModalForm" onSubmit={handleSubmit(raiseOnSaveTemplate)}>
                        <div className="d-md-flex flex-md-row flex-md-fill">
                            <div className="d-md-flex flex-md-column flex-md-grow-1">
                                <FormInput controlId="templateName" label="Name" fieldName="name" control={control} rules={{ required: { value: true, message: "Name is required." }, validate: validateName }} />
                            </div>
                            <div className="d-md-flex flex-md-column flex-md-shrink-1 justify-content-end">
                                <FormInput type="checkbox" controlId="templateActive" label="Active" fieldName="isActive" control={control} />
                                <FormInput type="checkbox" controlId="templatePrimary" label="Primary" fieldName="isDefault" control={control} />
                            </div>
                        </div>
                        <SchedulingPreferences
                            className="mt-2"
                            calendarKey={calendarKey}
                            editing={true}
                            hideActions={true}
                            schedulingColor={watchColor ?? ""}
                            schedulingName={watchName ?? ""}
                            setPreferencesStore={setSchedulePreferenceUI}
                            schedulePreferencesType={EntityScheduleType.Template}
                            schedulePreferences={schedulePreferences}
                            templateOrVetId={scheduleTemplateId}
                            hospitalHours={hospital.hospitalHours}
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className="btn-outline-primary" onClick={checkConfirmDirty}>Cancel</Button>
                    <Button form="locationPreferenceModalForm" variant="primary" type="submit">Save Preferences</Button>
                </Modal.Footer>
            </Modal >
        </>
    )
});
